import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { API } from '../../utils/api';
import axios from 'axios';
import { 
  Users, 
  MagnifyingGlass, 
  Crown, 
  CrownSimple,
  CheckCircle,
  Warning,
  CircleNotch,
  ChartBar,
  ArrowClockwise,
  Gift,
  Plus,
  Trash,
  Clock,
  CurrencyDollar,
  ChartLine
} from '@phosphor-icons/react';
import AnalyticsDashboard from '../../components/AnalyticsDashboard';

export default function AdminPanelPage() {
  const [adminKey, setAdminKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // User search
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // Toggle premium
  const [toggleEmail, setToggleEmail] = useState('');
  const [togglePremium, setTogglePremium] = useState(true);
  
  // Referrals
  const [referrals, setReferrals] = useState([]);
  const [showAddReferral, setShowAddReferral] = useState(false);
  const [newReferral, setNewReferral] = useState({
    referrer_email: '',
    referrer_name: '',
    friend_email: '',
    friend_name: ''
  });
  
  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Build an axios config that sends the admin key in the X-Admin-Key header
  // ONLY. Never in query params or JSON body. `withCredentials: false` avoids
  // sending the user's session cookie to admin endpoints (defense in depth).
  const adminAuth = useCallback((key = adminKey) => ({
    headers: { 'X-Admin-Key': key },
    withCredentials: false,
  }), [adminKey]);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleAuth = async () => {
    clearMessages();
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/admin/stats`, adminAuth(adminKey));
      setStats(response.data);
      setIsAuthenticated(true);
      loadReferrals(adminKey);
      loadAnalytics(adminKey);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid admin key');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async (key = adminKey) => {
    setAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics?days=7`, adminAuth(key));
      setAnalytics(response.data);
    } catch (err) {
      // silently ignore
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadReferrals = async (key = adminKey) => {
    try {
      const response = await axios.get(`${API}/admin/referrals`, adminAuth(key));
      setReferrals(response.data.referrals || []);
    } catch (err) {
      // silently ignore
    }
  };

  const refreshStats = async () => {
    clearMessages();
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/admin/stats`, adminAuth(adminKey));
      setStats(response.data);
      await loadReferrals();
      await loadAnalytics();
      setSuccess('Stats refreshed');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to refresh stats');
    } finally {
      setLoading(false);
    }
  };

  const addReferral = async () => {
    if (!newReferral.referrer_email || !newReferral.friend_email) {
      setError('Referrer and friend email are required');
      return;
    }
    
    clearMessages();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/referrals`, newReferral, adminAuth(adminKey));
      setSuccess('Referral added');
      setNewReferral({ referrer_email: '', referrer_name: '', friend_email: '', friend_name: '' });
      setShowAddReferral(false);
      await loadReferrals();
      await refreshStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add referral');
    } finally {
      setLoading(false);
    }
  };

  const updateReferralStatus = async (referralId, newStatus) => {
    clearMessages();
    setLoading(true);
    
    try {
      await axios.post(
        `${API}/admin/referrals/update-status`,
        { referral_id: referralId, status: newStatus },
        adminAuth(adminKey)
      );
      setSuccess(`Referral updated to ${newStatus}`);
      await loadReferrals();
      await refreshStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update referral');
    } finally {
      setLoading(false);
    }
  };

  const deleteReferral = async (referralId) => {
    if (!window.confirm('Delete this referral?')) return;
    
    clearMessages();
    setLoading(true);
    
    try {
      await axios.delete(`${API}/admin/referrals/${referralId}`, adminAuth(adminKey));
      setSuccess('Referral deleted');
      await loadReferrals();
      await refreshStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete referral');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = useCallback(async () => {
    clearMessages();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/users/search?email_search=${encodeURIComponent(searchEmail)}`,
        {},
        adminAuth(adminKey)
      );
      setSearchResults(response.data.users);
      if (response.data.count === 0) {
        setError('No users found');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [adminKey, searchEmail, adminAuth]);

  const toggleUserPremium = async (email = null, setPremium = null) => {
    clearMessages();
    setLoading(true);
    
    const targetEmail = email || toggleEmail;
    const targetStatus = setPremium !== null ? setPremium : togglePremium;
    
    try {
      const response = await axios.post(
        `${API}/admin/users/toggle-premium`,
        { user_email: targetEmail, set_premium: targetStatus },
        adminAuth(adminKey)
      );
      
      setSuccess(response.data.message);
      
      if (searchResults.length > 0) {
        await searchUsers();
      }
      
      await refreshStats();
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to toggle premium');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full"
        >
          <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            PaperPal Admin
          </h1>
          
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
              <Warning size={18} />
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Key
              </label>
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter admin key"
                onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
              />
            </div>
            
            <button
              onClick={handleAuth}
              disabled={loading || !adminKey}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <CircleNotch size={20} className="animate-spin" />
              ) : (
                'Authenticate'
              )}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">PaperPal Admin Panel</h1>
            <button
              onClick={() => setIsAuthenticated(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="p-4 rounded-lg bg-red-50 text-red-600 flex items-center gap-2">
            <Warning size={20} />
            {error}
          </div>
        )}
        {success && (
          <div className="p-4 rounded-lg bg-[#134E4A]/10 text-[#134E4A] flex items-center gap-2">
            <CheckCircle size={20} />
            {success}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ChartBar size={24} />
                Overview
              </h2>
              <button
                onClick={refreshStats}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <ArrowClockwise size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.users.total}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-amber-600">Premium Users</p>
                <p className="text-2xl font-bold text-amber-600">{stats.users.premium}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Free Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.users.free}</p>
              </div>
              <div className="bg-[#134E4A]/10 rounded-lg p-4">
                <p className="text-sm text-[#134E4A]">Successful Payments</p>
                <p className="text-2xl font-bold text-[#134E4A]">{stats.payments.successful}</p>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Dashboard */}
        <AnalyticsDashboard analytics={analytics} loading={analyticsLoading} />

        {/* Quick Toggle Premium */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Crown size={24} />
            Quick Premium Toggle
          </h2>
          
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="email"
              value={toggleEmail}
              onChange={(e) => setToggleEmail(e.target.value)}
              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="user@email.com"
            />
            
            <select
              value={togglePremium}
              onChange={(e) => setTogglePremium(e.target.value === 'true')}
              className="px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="true">Set Premium (Paid)</option>
              <option value="false">Set Free</option>
            </select>
            
            <button
              onClick={() => toggleUserPremium()}
              disabled={loading || !toggleEmail}
              className="px-6 py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <CircleNotch size={20} className="animate-spin" /> : <Crown size={20} />}
              Apply
            </button>
          </div>
        </div>

        {/* User Search */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users size={24} />
            Search Users
          </h2>
          
          <div className="flex gap-4 mb-6">
            <input
              type="text"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search by email..."
              onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
            />
            <button
              onClick={searchUsers}
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <CircleNotch size={20} className="animate-spin" /> : <MagnifyingGlass size={20} />}
              Search
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Onboarding</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((user) => (
                    <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">{user.email}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {user.first_name} {user.last_name}
                      </td>
                      <td className="py-3 px-4">
                        {user.is_paid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                            <Crown size={12} weight="fill" />
                            Premium
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                            <CrownSimple size={12} />
                            Free
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {user.onboarding_completed ? (
                          <CheckCircle size={18} className="text-[#134E4A]" weight="fill" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => toggleUserPremium(user.email, !user.is_paid)}
                          disabled={loading}
                          className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                            user.is_paid
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          }`}
                        >
                          {user.is_paid ? 'Remove Premium' : 'Grant Premium'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Users */}
        {stats?.recent_users && (
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Signups</h2>
            <div className="space-y-2">
              {stats.recent_users.map((user) => (
                <div 
                  key={user.user_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()} 
                      {user.is_paid && ' • Premium'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSearchEmail(user.email);
                      searchUsers();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referrals Section */}
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Gift size={24} />
              Referrals
              {stats?.referrals && (
                <span className="text-sm font-normal text-gray-500">
                  ({stats.referrals.total} total)
                </span>
              )}
            </h2>
            <button
              onClick={() => setShowAddReferral(!showAddReferral)}
              className="px-4 py-2 bg-[#134E4A] text-white rounded-lg font-medium hover:bg-[#0F3D3A] flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              Add Referral
            </button>
          </div>

          {/* Referral Stats */}
          {stats?.referrals && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <Clock size={20} className="text-yellow-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-yellow-700">{stats.referrals.pending}</p>
                <p className="text-xs text-yellow-600">Pending</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <Gift size={20} className="text-orange-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-orange-700">{stats.referrals.reward_due || 0}</p>
                <p className="text-xs text-orange-600">Reward Due</p>
              </div>
              <div className="bg-[#134E4A]/10 rounded-lg p-3 text-center">
                <CurrencyDollar size={20} className="text-[#134E4A] mx-auto mb-1" />
                <p className="text-xl font-bold text-[#134E4A]">{stats.referrals.rewarded}</p>
                <p className="text-xs text-[#134E4A]">Rewarded</p>
              </div>
            </div>
          )}

          {/* Add Referral Form */}
          {showAddReferral && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">New Referral</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Referrer Email *</label>
                  <input
                    type="email"
                    value={newReferral.referrer_email}
                    onChange={(e) => setNewReferral({...newReferral, referrer_email: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="referrer@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Referrer Name</label>
                  <input
                    type="text"
                    value={newReferral.referrer_name}
                    onChange={(e) => setNewReferral({...newReferral, referrer_name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Friend Email *</label>
                  <input
                    type="email"
                    value={newReferral.friend_email}
                    onChange={(e) => setNewReferral({...newReferral, friend_email: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="friend@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Friend Name</label>
                  <input
                    type="text"
                    value={newReferral.friend_name}
                    onChange={(e) => setNewReferral({...newReferral, friend_name: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Jane Doe"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={addReferral}
                  disabled={loading}
                  className="px-4 py-2 bg-[#134E4A] text-white rounded-lg font-medium hover:bg-[#0F3D3A] disabled:opacity-50 text-sm"
                >
                  {loading ? 'Adding...' : 'Add Referral'}
                </button>
                <button
                  onClick={() => {
                    setShowAddReferral(false);
                    setNewReferral({ referrer_email: '', referrer_name: '', friend_email: '', friend_name: '' });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Referrals Table */}
          {referrals.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Referrer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Friend</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Reward</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref) => (
                    <tr key={ref.referral_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-gray-900">{ref.referrer_name || ref.referrer_email}</p>
                        {ref.referrer_name && (
                          <p className="text-xs text-gray-500">{ref.referrer_email}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-gray-900">{ref.friend_name || ref.friend_email}</p>
                        {ref.friend_name && (
                          <p className="text-xs text-gray-500">{ref.friend_email}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <select
                          value={ref.status}
                          onChange={(e) => updateReferralStatus(ref.referral_id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${
                            ref.status === 'pending' 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : ref.status === 'reward_due'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-[#134E4A]/10 text-[#134E4A]'
                          }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="reward_due">Reward Due</option>
                          <option value="rewarded">Rewarded</option>
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-[#134E4A]">${ref.reward}</span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => deleteReferral(ref.referral_id)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete referral"
                        >
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Gift size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No referrals yet</p>
              <p className="text-xs">Click "Add Referral" to track a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
